// @ts-check
import Axios from 'axios';
import { serialize } from 'object-to-formdata';

import HTTP from '@/utils/http';
import { CreatingEnvelope } from '@/store/modules/envelopes/models';
import { Sharing } from '@/store/modules/users/models';
import { EnvelopeFields } from '@/store/modules/envelopes/models/EnvelopeFields';

import { documentsSchema } from '@/data/schema/documents';
import { creatingPartiesSchema } from '@/data/schema/parties/parties';

/**
 * @typedef {import('@/store/modules/envelopes/models/Envelope').TCreatingEnvelope} TCreatingEnvelope
 * @typedef {import('@/store/modules/envelopes/models/Envelope').TEnvelopeUpdate} TEnvelopeUpdate
 * @typedef {import('@/store/modules/envelopes/models/Envelope').TEnvelopeProgressUpdate} TEnvelopeProgressUpdate
 * @typedef {import('@/store/modules/envelopes/models/Envelope').TEnvelopeSend} TEnvelopeSend
 * @typedef {import('@/data/schema/documents').TDocuments} TDocuments
 * @typedef {import('@/data/schema/documents').TDocumentSummaries} TDocumentSummaries
 * @typedef {import('@/data/schema/documents').TUpdateDocuments} TUpdateDocuments
 * @typedef {import('@/data/schema/parties').TCreatingParties} TCreatingParties
 * @typedef {import('@/store/modules/users/models/Sharing').TSharing} TSharing
 * @typedef {import('@/store/modules/users/models/Sharing').TSharingUpdate} TSharingUpdate
 * @typedef {import('@/data/schema/parties').TNewOrSavedParties} TUpdateParties
 * @typedef {import('@/data/schema/parties').TPartiesUpdate} TPartiesUpdate
 * @typedef {import('@/store/modules/envelopes/models/EnvelopeFields').TEnvelopeFields} TEnvelopeFields
 * @typedef {import('@/store/modules/envelopes/models/EnvelopeFields').TEnvelopeFieldsUpdate} TEnvelopeFieldsUpdate
 */

/**
 * Fetch envelope
 *
 * @param {string} fingerprint
 * @param {AbortSignal} signal
 * @returns {Promise<TCreatingEnvelope>}
 */
export async function fetchEnvelope(fingerprint, signal) {
  // Create a new CancelToken source for this request
  const source = Axios.CancelToken.source();

  const response = await HTTP({
    url: `v2/envelopes/${fingerprint}`,
    cancelToken: source.token,
  });

  // Cancel the request if TanStack Query signals to abort
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query');
  });

  return CreatingEnvelope.parse(response.data.data);
}

/**
 * Create envelope
 *
 * @param {Partial<TEnvelopeUpdate & TEnvelopeProgressUpdate>} data
 * @returns {Promise<TCreatingEnvelope>}
 */
export async function postEnvelope(data) {
  const formData = serialize(data, { indices: true, booleansAsIntegers: true });

  const response = await HTTP({
    method: 'POST',
    url: 'v2/envelopes',
    data: formData,
  });

  return CreatingEnvelope.parse(response.data.data);
}

/**
 * Update envelope
 *
 * @param {string} fingerprint
 * @param {Partial<TEnvelopeUpdate & TEnvelopeProgressUpdate>} data
 * @returns {Promise<TCreatingEnvelope>}
 */
export async function patchEnvelope(fingerprint, data) {
  const formData = serialize(data, { indices: true, booleansAsIntegers: true });
  // Add the PATCH method override, as forms don't support every HTTP method
  formData.append('_method', 'PATCH');

  const response = await HTTP({
    method: 'POST',
    url: `v2/envelopes/${fingerprint}`,
    data: formData,
  });

  return CreatingEnvelope.parse(response.data.data);
}

/**
 * Send envelope
 *
 * @param {string} fingerprint
 * @param {TEnvelopeSend} data
 * @returns {Promise<TCreatingEnvelope>}
 */
export async function sendEnvelope(fingerprint, data) {
  const response = await HTTP({
    method: 'POST',
    url: `v2/envelopes/${fingerprint}/send`,
    data,
  });

  return CreatingEnvelope.parse(response.data.data);
}

/**
 * Fetch documents
 *
 * @param {string} fingerprint
 * @param {AbortSignal} signal
 * @returns {Promise<TDocuments>}
 */
export async function fetchEnvelopeDocuments(fingerprint, signal) {
  // Create a new CancelToken source for this request
  const source = Axios.CancelToken.source();

  const response = await HTTP({
    url: `v2/envelopes/${fingerprint}/documents`,
    cancelToken: source.token,
  });

  // Cancel the request if TanStack Query signals to abort
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query');
  });

  return documentsSchema.parse(response.data.data);
}

/**
 * Update envelope documents
 *
 * @param {string} fingerprint
 * @param {{documents: TUpdateDocuments}} data
 * @returns {Promise<TDocuments>}
 */
export async function postEnvelopeDocuments(fingerprint, data) {
  const formData = serialize(data, { indices: true, booleansAsIntegers: true });

  const response = await HTTP({
    method: 'POST',
    url: `v2/envelopes/${fingerprint}/documents`,
    data: formData,
  });

  return documentsSchema.parse(response.data.data);
}

/**
 * Fetch parties
 *
 * @param {string} fingerprint
 * @param {AbortSignal} signal
 * @returns {Promise<TCreatingParties>}
 */
export async function fetchEnvelopeParties(fingerprint, signal) {
  // Create a new CancelToken source for this request
  const source = Axios.CancelToken.source();

  const response = await HTTP({
    url: `v2/envelopes/${fingerprint}/parties`,
    cancelToken: source.token,
  });

  // Cancel the request if TanStack Query signals to abort
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query');
  });

  return creatingPartiesSchema.parse(response.data.data);
}

/**
 * Fetch envelope
 *
 * @param {string} fingerprint
 * @param {AbortSignal} signal
 * @returns {Promise<TSharing>}
 */
export async function fetchEnvelopeSharing(fingerprint, signal) {
  // Create a new CancelToken source for this request
  const source = Axios.CancelToken.source();

  const response = await HTTP({
    url: `v2/envelopes/${fingerprint}/sharing`,
    cancelToken: source.token,
  });

  // Cancel the request if TanStack Query signals to abort
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query');
  });

  return Sharing.parse(response.data.data);
}

/**
 * Update envelope parties
 *
 * @param {string} fingerprint
 * @param {Omit<TPartiesUpdate, 'parallel_signing'>} data
 * @returns {Promise<TCreatingParties>}
 */
export async function putEnvelopeParties(fingerprint, data) {
  const response = await HTTP({
    method: 'PUT',
    url: `v2/envelopes/${fingerprint}/parties`,
    data,
  });

  return creatingPartiesSchema.parse(response.data.data);
}

/**
 * Update envelope sharing
 *
 * @param {string} fingerprint
 * @param {TSharingUpdate} data
 * @returns {Promise<TSharing>}
 */
export async function postEnvelopeSharing(fingerprint, data) {
  const response = await HTTP({
    method: 'POST',
    url: `v2/envelopes/${fingerprint}/sharing`,
    data,
  });

  return Sharing.parse(response.data.data);
}

/**
 * Fetch envelope fields
 *
 * @param {string} fingerprint
 * @param {AbortSignal} signal
 * @returns {Promise<TEnvelopeFields>}
 */
export async function fetchEnvelopeFields(fingerprint, signal) {
  // Create a new CancelToken source for this request
  const source = Axios.CancelToken.source();

  const response = await HTTP({
    url: `v2/envelopes/${fingerprint}/fields`,
    params: {
      only_prefill: 1,
    },
    cancelToken: source.token,
  });

  // Cancel the request if TanStack Query signals to abort
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query');
  });

  return EnvelopeFields.parse(response.data.data);
}

/**
 * Set the values for an envelopes prefillable fields
 *
 * @param {string} fingerprint
 * @param {TEnvelopeFieldsUpdate} data
 * @returns {Promise<TEnvelopeFields>}
 */
export async function patchEnvelopeFields(fingerprint, data) {
  const response = await HTTP({
    method: 'PATCH',
    url: `v2/envelopes/${fingerprint}/fields/values`,
    data,
  });

  return EnvelopeFields.parse(response.data.data);
}
